import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import { Badge } from '@r/components/ui/badge.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import type { DataTableFilterField } from '@r/types/index.ts';

export interface TargetCollection {
  id?: number;
  company?: { name?: string | null; uniqueCode?: string | null } | null;
  logicTypeTarget?: { reference?: string | null; description?: string | null } | null;
  value?: number | null;
  targetValue?: number | null;
  achievedPercentage?: number | null;
  achievedAt?: string | null;
  updatedAt?: string | null;
}

export const useTargetsFilterFields = (): DataTableFilterField<TargetCollection>[] => {
  const { t } = useTranslation();

  const achievedOptions = [
    { label: t('label.target.achieved'), value: 'true' },
    { label: t('label.target.not.achieved'), value: 'false' }
  ];

  return [
    {
      label: t('label.company.name'),
      value: 'companyName',
      placeholder: t('label.company.name')
    },
    {
      label: t('label.company.unique.code'),
      value: 'companyUniqueCode',
      placeholder: t('label.company.unique.code')
    },
    {
      label: t('label.performances.target.reference'),
      value: 'logicTypeTargetReference',
      placeholder: t('label.performances.target.reference')
    },
    {
      label: t('label.target.status'),
      value: 'achieved',
      options: achievedOptions
    }
  ];
};

export const useTargetsColumns = (isTargetPointLogic = false): ColumnDef<TargetCollection>[] => {
  const { t } = useTranslation();

  return [
    {
      id: 'companyName',
      accessorKey: 'company.name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.company.name')} />
      ),
      enableHiding: false,
      cell: ({ row }) => row.original.company?.name ?? ''
    },
    {
      id: 'companyUniqueCode',
      accessorKey: 'company.uniqueCode',
      header: t('label.company.unique.code'),
      enableSorting: false,
      cell: ({ row }) => row.original.company?.uniqueCode ?? ''
    },
    {
      id: 'logicTypeTargetReference',
      accessorKey: 'logicTypeTarget.reference',
      header: t('label.performances.target.reference'),
      enableSorting: false,
      cell: ({ row }) => row.original.logicTypeTarget?.reference ?? ''
    },
    {
      id: 'logicTypeTargetDescription',
      accessorKey: 'logicTypeTarget.description',
      header: t('label.target.description'),
      enableSorting: false,
      cell: ({ row }) => (
        <span
          className="block max-w-[200px] truncate"
          title={row.original.logicTypeTarget?.description ?? undefined}
        >
          {row.original.logicTypeTarget?.description}
        </span>
      )
    },
    {
      id: 'value',
      accessorKey: 'value',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.target.value')} />
      ),
      cell: ({ row }) => (
        <FormattedNumber
          value={row.original.value ?? 0}
          style={isTargetPointLogic ? 'decimal' : undefined}
          suffix={
            isTargetPointLogic ? ` ${pointsTranslation(t, row.original.value, false)}` : undefined
          }
        />
      )
    },
    {
      id: 'targetValue',
      accessorKey: 'targetValue',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.target.goal')} />
      ),
      cell: ({ row }) => (
        <FormattedNumber
          value={row.original.targetValue ?? 0}
          style={isTargetPointLogic ? 'decimal' : undefined}
          suffix={
            isTargetPointLogic
              ? ` ${pointsTranslation(t, row.original.targetValue, false)}`
              : undefined
          }
        />
      )
    },
    {
      id: 'achievedPercentage',
      accessorKey: 'achievedPercentage',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.target.progress')} />
      ),
      cell: ({ row }) => `${Math.round(row.original.achievedPercentage ?? 0)}%`
    },
    {
      id: 'achieved',
      accessorKey: 'achievedAt',
      header: t('label.target.status'),
      enableSorting: false,
      cell: ({ row }: CellContext<TargetCollection, unknown>) => (
        <Badge variant={row.original.achievedAt ? 'default' : 'secondary'}>
          {row.original.achievedAt ? t('label.target.achieved') : t('label.target.not.achieved')}
        </Badge>
      )
    },
    {
      id: 'updatedAt',
      accessorKey: 'updatedAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.target.updated.at')} />
      ),
      cell: ({ row }) =>
        row.original.updatedAt
          ? format(new Date(row.original.updatedAt), 'dd/MM/yyyy', { locale: fr })
          : ''
    }
  ];
};
